/**
 * BookmarkManagerDialog — Popup dialog to add, edit, jump-to, and delete bookmarks.
 * Opens when the Bookmark tool button is clicked while already active,
 * or via store.setBookmarkDialogOpen(true).
 * Follows the glassmorphism style of SavePatternDialog / FileManagerDialog.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font, ui, z } from '../ui/theme';

export default function BookmarkManagerDialog() {
  const open = useEditorStore(s => s.bookmarkDialogOpen);
  const setOpen = useEditorStore(s => s.setBookmarkDialogOpen);
  const bookmarks = useEditorStore(s => s.bookmarks);
  const addBookmark = useEditorStore(s => s.addBookmark);
  const removeBookmark = useEditorStore(s => s.removeBookmark);
  const updateBookmarkName = useEditorStore(s => s.updateBookmarkName);
  const requestClearBookmarks = useEditorStore(s => s.requestClearBookmarks);
  // Only track the live beat while the dialog is open; a constant 0 otherwise
  // so playback at 60fps doesn't re-render a closed dialog.
  const currentBeat = useEditorStore(s => (s.bookmarkDialogOpen ? s.playback.currentBeat : 0));

  const [newName, setNewName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setNewName('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, setOpen]);

  const handleAdd = useCallback(() => {
    const beat = useEditorStore.getState().playback.currentBeat;
    addBookmark(beat, newName.trim());
    setNewName('');
    inputRef.current?.focus();
  }, [addBookmark, newName]);

  const handleJumpTo = useCallback((beat: number) => {
    const store = useEditorStore.getState();
    const bpm = store.beatmap?.bpm || 120;
    const offset = store.beatmap?.offset || 0;
    const time = (beat / (bpm / 60)) + (offset / 1000);
    store.setCurrentTime(Math.max(0, time));
  }, []);

  const handleClearAll = useCallback(() => {
    if (bookmarks.length === 0) return;
    requestClearBookmarks();
  }, [requestClearBookmarks, bookmarks.length]);

  if (!open) return null;

  const sorted = [...bookmarks].sort((a, b) => a.beat - b.beat);

  return (
    <div style={overlayStyle} onClick={() => setOpen(false)}>
      <div style={dialogStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <span style={{ fontWeight: 600, fontSize: 14, color: color.ink }}>
            {'\uD83D\uDD16'} Bookmarks
          </span>
          <button style={closeBtnStyle} onClick={() => setOpen(false)}>
            {'\u2715'}
          </button>
        </div>

        {/* Add form */}
        <div style={addFormStyle}>
          <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="Bookmark name (optional)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
              style={inputStyle}
            />
            <div style={beatBadgeStyle}>
              Beat {currentBeat.toFixed(1)}
            </div>
          </div>
          <button style={addBtnStyle} onClick={handleAdd}>
            + Add
          </button>
        </div>

        {/* Bookmark list */}
        <div style={listContainerStyle}>
          {sorted.length === 0 ? (
            <div style={emptyStyle}>
              No bookmarks yet. Use the form above or click in the 3D view with the bookmark tool.
            </div>
          ) : (
            sorted.map((bm, i) => (
              <BookmarkRow
                key={`${bm.beat}-${i}`}
                beat={bm.beat}
                name={bm.name}
                onNameChange={(name) => updateBookmarkName(bm.beat, name)}
                onJumpTo={() => handleJumpTo(bm.beat)}
                onDelete={() => removeBookmark(bm.beat)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {sorted.length > 0 && (
          <div style={footerStyle}>
            <span style={{ fontSize: 11, color: color.inkFaint }}>
              {sorted.length} bookmark{sorted.length !== 1 ? 's' : ''}
            </span>
            <button style={clearBtnStyle} onClick={handleClearAll}>
              Clear All
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bookmark Row ──────────────────────────────────────────────────────────────

function BookmarkRow({
  beat, name, onNameChange, onJumpTo, onDelete,
}: {
  beat: number;
  name: string;
  onNameChange: (name: string) => void;
  onJumpTo: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(name);
  const editRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) {
      setEditValue(name);
      setTimeout(() => editRef.current?.focus(), 50);
    }
  }, [editing, name]);

  const commitEdit = () => {
    onNameChange(editValue.trim());
    setEditing(false);
  };

  return (
    <div style={rowStyle}>
      <div style={rowBeatStyle}>{beat.toFixed(2)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input
            ref={editRef}
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(false); }}
            style={rowInputStyle}
          />
        ) : (
          <span
            style={{ ...rowNameStyle, cursor: 'pointer' }}
            onDoubleClick={() => setEditing(true)}
            title="Double-click to rename"
          >
            {name || '(unnamed)'}
          </span>
        )}
      </div>
      <button style={rowBtnStyle} onClick={onJumpTo} title="Jump to this beat">
        {'\u25B6'}
      </button>
      <button style={{ ...rowBtnStyle, color: '#F87171' }} onClick={onDelete} title="Delete bookmark">
        {'\u2715'}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = { ...ui.overlay };

const dialogStyle: React.CSSProperties = { ...ui.dialog, width: '90%', maxWidth: 520, maxHeight: '70vh' };

const headerStyle: React.CSSProperties = { ...ui.dialogHeader, padding: '12px 16px' };

const closeBtnStyle: React.CSSProperties = { ...ui.closeBtn };

const addFormStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)',
};

const inputStyle: React.CSSProperties = { ...ui.input, flex: 1 };

const beatBadgeStyle: React.CSSProperties = {
  background: 'rgba(167, 139, 250, 0.15)',
  border: '1px solid rgba(167, 139, 250, 0.25)',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 10,
  color: color.accentSoft,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const addBtnStyle: React.CSSProperties = {
  background: 'rgba(167, 139, 250, 0.2)',
  border: '1px solid rgba(167, 139, 250, 0.3)',
  borderRadius: 6,
  padding: '6px 12px',
  color: color.accentSoft,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: font.sans,
};

const listContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '4px 0',
  minHeight: 80,
  maxHeight: 300,
};

const emptyStyle: React.CSSProperties = {
  padding: '24px 16px',
  textAlign: 'center',
  color: color.inkFaint,
  fontSize: 12,
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  borderTop: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)',
};

const clearBtnStyle: React.CSSProperties = {
  background: 'rgba(248, 113, 113, 0.1)',
  border: '1px solid rgba(248, 113, 113, 0.2)',
  borderRadius: 6,
  padding: '4px 10px',
  color: '#F87171',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: font.sans,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 16px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.04)',
};

const rowBeatStyle: React.CSSProperties = {
  width: 52,
  fontSize: 11,
  fontWeight: 600,
  color: color.accentSoft,
  fontFamily: "'JetBrains Mono', monospace",
  textAlign: 'right',
  flexShrink: 0,
};

const rowNameStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#C4B5FD',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowInputStyle: React.CSSProperties = {
  ...ui.input,
  width: '100%', borderRadius: 4, padding: '3px 6px',
};

const rowBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: color.accentSoft,
  fontSize: 12,
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  flexShrink: 0,
};
